/* eslint-disable react/button-has-type */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable no-empty */
/* eslint-disable consistent-return */
/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-boolean-value */
// Deck.gl + Mapbox GL JS (no react-map-gl)
// MUI v6+, Redux integration, DataFilterExtension

import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { ScatterplotLayer } from '@deck.gl/layers';
import { DataFilterExtension } from '@deck.gl/extensions';
import GUI from './gui';
import {
  getMapboxToken,
  formatDayLabel,
  toRGB,
  toRGBA,
} from '../../utils/functions';
import { MapDeckStyles } from './mapDeckStyles';
import { PRESET_BOUNDS, FIT_OPTIONS } from './viewPortPresets';
import { getFillColorByEvent } from '../header/legend';
import slices from '../../state/slices/data';

// choose which preset to show on first load
const INITIAL_PRESET = 'Middle East';

const MAPBOX_ACCESS_TOKEN = getMapboxToken();
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const INITIAL_VIEW_STATE = {
  longitude: 35.668358502045116,
  latitude: 33.917171006901405,
  zoom: 3,
  pitch: 0,
  bearing: 0,
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const lerp = (a, b, t) => a + (b - a) * t;

export default function MapDeck({ topBias = 0.15 }) {
  const dispatch = useDispatch();

  // Redux state
  const data = useSelector((state) => state.data?.data) || []; // <-- array fallback
  const timeRange = useSelector((state) => state.data?.timeRange);
  const filterRange = useSelector((state) => state.data?.filterRange);
  const animationSpeed = useSelector((state) => state.data?.animationSpeed);
  const isLoading = useSelector((state) => state.data?.isLoading);
  const isAnimating = useSelector((s) => s.data.isAnimating);

  const [mapReady, setMapReady] = useState(false);
  const [selected, setSelected] = useState(null); // {lng, lat, datum} or null
  const [bias, setBias] = useState(0); // 0 = center; 0.25 ≈ 25% towards top
  const TOOLTIP_OFFSET_PX = 10;

  const {
    setFilterRange,
    setViewState,
    setAnimating,
    setAnimationSpeed,
    toggleAnimating,
  } = slices.actions;

  const viewState =
    useSelector((state) => state.ui?.viewState) || INITIAL_VIEW_STATE;

  // Refs
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const lastSpeedRef = useRef(null);
  const rafTicking = useRef(false);

  const getTooltip = ({ object }) => {
    if (!object) return null;
    return {
      html: `
        <div></div>
      `,
      style: {
        display: 'none',
      },
    };
  };

  // Close tooltip when animation starts (false -> true)
  const wasAnimatingRef = useRef(isAnimating);
  useEffect(() => {
    if (!wasAnimatingRef.current && isAnimating) {
      // going from paused to playing
      if (selected) setSelected(null);
    }
    wasAnimatingRef.current = isAnimating;
  }, [isAnimating, selected]);

  // Time bounds from data (days since 2023-10-07)
  const timeBounds = useMemo(() => {
    if (!data.length) return [0, 0];
    const days = data.map((d) => Number(d.day)).filter(Number.isFinite);
    return [Math.min(...days), Math.max(...days)];
  }, [data]);

  const currentDay = useMemo(() => {
    if (
      Array.isArray(filterRange) &&
      filterRange.length === 2 &&
      Number.isFinite(filterRange[1])
    ) {
      return filterRange[1];
    }

    return timeBounds[1];
  }, [filterRange, timeBounds]);

  // Safe filter range that falls back to the last valid range or defaults if data is missing
  const safeRange = useMemo(() => {
    const [minDay, maxDay] = timeBounds;

    if (!Number.isFinite(minDay) || !Number.isFinite(maxDay)) return [0, 0];

    if (
      Array.isArray(filterRange) &&
      filterRange.length === 2 &&
      Number.isFinite(filterRange[0]) &&
      Number.isFinite(filterRange[1])
    ) {
      return filterRange;
    }

    return [Math.max(minDay, maxDay - 2), maxDay];
  }, [filterRange, timeBounds]);

  // Deck layers (single memo; no nesting, no duplicate names)
  const layers = useMemo(() => {
    if (!data.length) return [];

    const dataFilterExtension = new DataFilterExtension({ filterSize: 1 });

    return [
      new ScatterplotLayer({
        id: 'scatter-plot',
        data,
        pickable: true,
        opacity: 0.8,
        stroked: true,
        filled: true,
        radiusScale: 2,
        // radiusMinPixels: 2,
        // radiusMaxPixels: 100,
        lineWidthMinPixels: 1,
        getPosition: (d) => (Array.isArray(d.position) ? d.position : null),
        getRadius: 2,
        radiusUnits: 'pixels',
        getLineColor: [255, 255, 255],
        lineWidthUnits: 'pixels',
        // redo getFillColor based on the 'source' property
        getFillColor: (d) => {
          console.log(
            'event type:',
            d.subEventType,
            getFillColorByEvent(d.subEventType),
          );
          return getFillColorByEvent(d.subEventType);
        },

        // Data filtering on "day" (small integer)
        extensions: [dataFilterExtension],
        getFilterValue: (d) => {
          const v = Number(d.day);
          return Number.isFinite(v) ? v : -Infinity; // never undefined
        },
        filterRange: safeRange,
        filterSoftRange: [
          safeRange[0] + (safeRange[1] - safeRange[0]) * 0.8,
          safeRange[1],
        ],
        filterTransformSize: true,
        filterTransformColor: true,
        // onHover: () => {},
        autoHighlight: false,
        onClick: (info) => {
          if (!info?.object || !mapRef.current || !mapContainerRef.current)
            return;

          dispatch(setAnimating(false));

          const map = mapRef.current;
          const [lng, lat] = info.object.position;

          // screen dims
          const w = map.getContainer().clientWidth || 0;
          const h = map.getContainer().clientHeight || 0;

          // fraction from top you want the point at (0 = top, 0.5 = middle, 1 = bottom)
          const biasClamped = Math.max(0, Math.min(1, topBias));
          const desired = [w / 2, biasClamped * h]; // desired screen position of the point

          // current projected screen position of the clicked lng/lat
          const p = map.project([lng, lat]); // -> {x, y}

          // vector we need to pan the *center* by (in screen pixels)
          const dx = p.x - desired[0];
          const dy = p.y - desired[1];

          // the current center (in screen px) is (w/2, h/2). Move it by (dx, dy)
          const newCenterPx = [w / 2 + dx, h / 2 + dy];

          // convert back to lng/lat and animate
          const newCenterLngLat = map.unproject(newCenterPx);

          map.easeTo({
            center: newCenterLngLat,
            duration: 600,
            essential: true,
          });

          console.log('clicked datum:', info.object.subEventType);
          const rgbArr = getFillColorByEvent(info.object.subEventType);
          const accent = toRGB(rgbArr);
          const accentA = toRGBA(rgbArr, 0.15); // nice translucent bg if you want

          setSelected({
            lng,
            lat,
            datum: info.object,
            bias: biasClamped,
            accent,
            accentA,
          });
        },
      }),
    ];
  }, [data, safeRange, bias]);

  // Initialize Mapbox map + Deck overlay
  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      // style: 'mapbox://styles/mapbox/light-v10',
      style: 'mapbox://styles/sbsonline/cmfhb0qyx002f01rfanldb24k',
      projection: 'mercator',
      center: [viewState.longitude, viewState.latitude],
      zoom: viewState.zoom,
      minZoom: 2,
      maxZoom: 16,
      // maxBounds: [[-180, -85], [180, 85]], // prevent going off the map
      pitch: viewState.pitch,
      bearing: viewState.bearing,
      attributionControl: true,
      cooperativeGestures: true,
      logoPosition: 'bottom-left',
    });

    const computeSpeed = (zoom) => {
      if (zoom <= 2) return 0.2;
      if (zoom >= 7) return 0.5;
      const t = (zoom - 2) / (7 - 2); // 0..1
      return lerp(0.2, 0.5, t);
    };

    const updateSpeed = () => {
      rafTicking.current = false;
      const z = map.getZoom();
      const speed = computeSpeed(z);

      if (lastSpeedRef.current !== speed) {
        lastSpeedRef.current = speed;
        dispatch(setAnimationSpeed(speed));
      }
    };

    const onZoom = () => {
      // throttle to animation frames while zooming
      if (!rafTicking.current) {
        rafTicking.current = true;
        requestAnimationFrame(updateSpeed);
      }
    };

    // dynamic updates while zooming
    map.on('zoom', onZoom);

    map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
      }),
      'top-left',
    );
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    map.scrollZoom.disable();

    mapRef.current = map;

    const overlay = new MapboxOverlay({
      interleaved: true,
      layers, // use the memoized layers
      // getTooltip: null,
    });
    overlayRef.current = overlay;
    map.addControl(overlay);

    const handleMove = () => {
      const center = map.getCenter();
      dispatch(
        setViewState({
          longitude: center.lng,
          latitude: center.lat,
          zoom: map.getZoom(),
          pitch: map.getPitch(),
          bearing: map.getBearing(),
        }),
      );
    };

    map.on('moveend', handleMove);
    map.on('load', () => {
      // Initial fit to the same bounds/padding as your buttons
      map.fitBounds(PRESET_BOUNDS[INITIAL_PRESET], {
        ...FIT_OPTIONS,
        // If you prefer no animation on first paint:
        duration: 0,
      });
      updateSpeed();
      setMapReady(true);

      // if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // dispatch(toggleAnimating()); // your Redux action to begin
    });

    // 👇 close tooltip when user interacts
    map.on('dragstart', () => setSelected(null));
    map.on('mousedown', () => setSelected(null));
    map.on('touchstart', () => setSelected(null));

    map.on('load', () => setMapReady(true));

    return () => {
      map.off('moveend', handleMove);
      if (overlay) {
        try {
          map.removeControl(overlay);
        } catch {}
      }
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
    };
  }, []); // init once

  // Update Deck overlay when layers or tooltip change
  useEffect(() => {
    if (!overlayRef.current) return;
    overlayRef.current.setProps({
      layers,
      getTooltip: null,
    });
  }, [layers, getTooltip]);

  // Keep Mapbox view in sync if Redux viewState changes elsewhere
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const current = {
      longitude: map.getCenter().lng,
      latitude: map.getCenter().lat,
      zoom: map.getZoom(),
      pitch: map.getPitch(),
      bearing: map.getBearing(),
    };

    const changed =
      Math.abs(current.longitude - viewState.longitude) > 1e-6 ||
      Math.abs(current.latitude - viewState.latitude) > 1e-6 ||
      Math.abs(current.zoom - viewState.zoom) > 1e-6 ||
      Math.abs(current.pitch - viewState.pitch) > 1e-6 ||
      Math.abs(current.bearing - viewState.bearing) > 1e-6;

    if (changed) {
      map.easeTo({
        center: [viewState.longitude, viewState.latitude],
        zoom: viewState.zoom,
        pitch: viewState.pitch,
        bearing: viewState.bearing,
        duration: 500,
      });
    }
  }, [viewState]);

  const handleFilterChange = (newRange) => {
    if (
      Array.isArray(newRange) &&
      newRange.length === 2 &&
      Number.isFinite(newRange[0]) &&
      Number.isFinite(newRange[1])
    ) {
      dispatch(setFilterRange(newRange));
    }
  };

  const fmtCoord = (n) => (Number.isFinite(n) ? n.toFixed(4) : '—');

  return (
    <MapDeckStyles>
      <div className="control-panel">
        {isLoading ? (
          <div>Loading data...</div>
        ) : (
          data.length > 0 && (
            <GUI
              min={timeBounds[0]}
              max={timeBounds[1]}
              value={safeRange}
              animationSpeed={animationSpeed}
              onChange={handleFilterChange}
              formatLabel={(d) => formatDayLabel(d)}
            />
          )
        )}
      </div>
      <div id="map-wrap">
        <div
          ref={mapContainerRef}
          className={`mapContainer ${mapReady ? 'fade-in' : ''}`}
        />

        {/* Centered tooltip that sits 10px BELOW the point’s screen location */}
        {selected && (
          <div
            className="click-tooltip"
            // left: 50% centers horizontally
            // top: 50% (screen center) - biasPx (point appears higher) + 10px (below the point)
            style={{
              left: '50%',
              top: `calc(${selected.bias * 100}% + ${TOOLTIP_OFFSET_PX}px)`,
              // expose the color as CSS vars for child styles
              '--accent': selected.accent || 'rgb(200,200,200)',
              '--accentA': selected.accentA || 'rgba(200,200,200,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tt-inner">
              <div
                className="tt-inner-wrap"
                style={{
                  borderLeft: '5px solid var(--accent)',
                  padding: '0 0 0 15px',
                  marginLeft: '5px',
                }}
              >
                <div className="tt-row large">{selected.datum.location}</div>
                <div className="tt-row small">{selected.datum.country}</div>
                {selected.datum?.day != null && (
                  <div className="tt-row">
                    <b>Date:</b> {formatDayLabel(selected.datum.day)}
                  </div>
                )}
                <div
                  className="tt-body"
                  onWheel={(e) => e.stopPropagation()} // prevent map zoom on wheel
                  onTouchMove={(e) => e.stopPropagation()} // prevent map pan on touch scroll
                >
                  <div className="tt-row">
                    <p>{selected.datum.notes}</p>
                  </div>
                  <div className="tt-row source">
                    <strong>Source:</strong> {selected.datum.source}
                  </div>
                </div>
                <button className="tt-close" onClick={() => setSelected(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MapDeckStyles>
  );
}
